import { useEffect } from 'react'
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Switch } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import type { OperationResult } from '@/shared/api/operationResult'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { useAuth } from '@/shared/auth/useAuth'
import { SiteSelect } from '@/features/sites/SiteSelect'
import {
  DEVICE_CONNECTION_TYPES,
  DEVICE_DIRECTIONS,
  DEVICE_TIME_ZONES,
  ISUP_DEVICE_ID_MAX,
  ISUP_KEY_MAX,
  buildDeviceDraft,
  buildDeviceUpdate,
  createDevice,
  formatWebhookAddress,
  gatewayErrorCode,
  generateIsupDeviceId,
  generateIsupKey,
  isValidDeviceCode,
  isValidIpv4,
  isValidIsupDeviceId,
  isValidIsupKey,
  parseWebhookAddress,
  updateDevice,
  type AttendanceDevice,
  type DeviceConnectionType,
  type DeviceDirection,
} from './attendanceApi'

interface DeviceFormModalProps {
  open: boolean
  /** `null` = yangi qurilma */
  device: AttendanceDevice | null
  onClose: () => void
  /**
   * Saqlash muvaffaqiyatli tugagach chaqiriladi.
   *
   * ⚠️ Nima uchun kerak: `ISUP_GATEWAY` qurilmasi saqlangach texnikka
   * beriladigan to'rtta qiymat DARHOL ko'rsatilishi kerak («Ulash
   * ma'lumotlari» oynasi). Uni operator o'zi qidirib topishiga
   * qoldirish eng ko'p uchraydigan yo'qotish nuqtasi bo'lardi: qurilma
   * yaratilgan, lekin hech kim uni kameraga ulamagan.
   *
   * ⚠️ Ikkinchi parametr — TAHRIRDAGI natija (2026-08-15, D4). Yaratishda
   * `null`: `POST` javobi holat qaytarmaydi. Tahrirda esa u `PARTIAL`
   * bo'lishi mumkin (`ISUP_GATEWAY → DIRECT_ISAPI`, gateway'dan o'chirish
   * yiqildi) va sahifa uni SARIQ ogohlantirish bilan ko'rsatishi kerak —
   * `CLAUDE.md` §3.2, 4-qoida.
   *
   * ⚠️ Natija SAHIFAGA uzatiladi, modalda ko'rsatilmaydi: modal saqlashdan
   * keyin YOPILADI, ya'ni ichida chizilgan ogohlantirish bir lahzada
   * yo'qolardi — foydalanuvchi uni umuman o'qiy olmasdi.
   */
  onSaved?: (device: AttendanceDevice, result: OperationResult | null) => void
}

interface FormValues {
  name: string
  deviceId: string
  /**
   * ⚠️ SERVER QURILMAGA QANDAY YETADI — formaning eng ustidagi tanlov,
   * chunki u QOLGAN MAYDONLARNI belgilaydi (`attendanceApi` dagi
   * `DEVICE_CONNECTION_TYPES` izohi).
   */
  connectionType: DeviceConnectionType
  /** ⚠️ FAQAT `DIRECT_ISAPI` da — gateway rejimida bu maydonlar CHIZILMAYDI */
  ipAddress?: string
  port?: number
  username?: string
  /** ⚠️ FAQAT `ISUP_GATEWAY` da — kamera menyusiga teriladi */
  isupDeviceId?: string
  /** ⚠️ FAQAT `ISUP_GATEWAY` da — kamera menyusiga teriladi */
  isupKey?: string
  password?: string
  timeZone: string
  fdId: string
  doorNo: number
  direction: DeviceDirection
  /**
   * ⚠️ TERMINAL QAYERDA TURIBDI — `ipAddress` javob bermaydigan savol.
   *
   * `null` = biriktirilmagan va bu HAQIQIY holat (ustunda `NOT NULL`
   * yo'q, `DeviceDraft.siteId` ixtiyoriy). Tozalash — ATAYLAB
   * qilinadigan amal (`allowClear`), tasodifiy emas: maydonning o'zi
   * so'rovda HAR DOIM bo'ladi (`buildDeviceCommon`).
   */
  siteId?: string | null
  /**
   * ⚠️ `host:port` BITTA maydonda — `parseWebhookAddress` izohi.
   * Bo'sh qoldirilsa global sozlama ishlatiladi.
   */
  webhookAddress?: string
  active: boolean
}

/** Yangi qurilma uchun standart qiymatlar — migratsiyadagi `DEFAULT` lar bilan bir xil */
const DEFAULTS = {
  /**
   * ⚠️ `DIRECT_ISAPI` — STANDART va u shunday qoladi. Bu "hozircha
   * shunday" emas: VPN'li filial hozir ham, keyin ham eng ko'p
   * uchraydigan holat, va standartni almashtirish mavjud oqimning
   * har bir qadamini o'zgartirardi.
   */
  connectionType: 'DIRECT_ISAPI',
  port: 80,
  fdId: '1',
  doorNo: 1,
  /**
   * ⚠️ `BOTH` — "yo'nalish ma'lum emas". `IN` ni standart qilish eng
   * vasvasali variant (aksariyat terminal kirish eshigida turadi) va
   * u ENG XAVFLISI: operator o'ylamay saqlasa har chiqish "ishga
   * kelish" bo'lib yozilardi va hech kim binodan chiqmagandek
   * ko'rinardi — JIMGINA, hech qanday xatosiz.
   */
  direction: 'BOTH',
  /** Joylashuv TAXMIN QILINMAYDI — birinchisini tanlash yolg'on ma'lumot bo'lardi */
  siteId: null,
  active: true,
} as const

/**
 * Davomat terminalini qo'shish va tahrirlash.
 *
 * <h2>⚠️ PAROL MAYDONI TAHRIRLASHDA BO'SH KELADI</h2>
 * Server parolni HECH QACHON qaytarmaydi (`attendanceApi.ts` sarlavhasi),
 * ya'ni formani oldindan to'ldirish texnik jihatdan MUMKIN EMAS. Shuning
 * uchun qoida: bo'sh qoldirilsa — parol o'zgarmaydi.
 *
 * Bu foydalanuvchiga OCHIQ aytiladi (`passwordEditHint`), chunki bo'sh
 * parol maydoni ikki xil o'qiladi: "parol qo'yilmagan" va "o'zgartirmasangiz
 * bo'ladi". Birinchi o'qish operatorni har tahrirda parolni qaytadan
 * terishga majbur qilardi — va bir marta xato tersa, Hikvision manzilni
 * ~30 daqiqaga qulflab qo'yardi.
 *
 * `NasFormModal` da bu masala BOSHQACHA hal qilingan (u yerda sir maydoni
 * tahrirlashda umuman CHIZILMAYDI, almashtirish esa alohida oyna). Farqning
 * sababi kontraktda: `PUT /api/nas/{id}` sirni umuman qabul qilmaydi, bu
 * yerda esa `PUT` parolni ixtiyoriy maydon sifatida qabul qiladi. Ya'ni
 * ikkalasi ham o'z serveriga mos, va ikkalasida ham parol EKRANDA
 * ko'rsatilmaydi.
 */
export function DeviceFormModal({ open, device, onClose, onSaved }: DeviceFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const tenantZone = useAuth().user?.tenant.timezone
  const defaultTimeZone = DEVICE_TIME_ZONES.find((zone) => zone === tenantZone)

  /*
    ⚠️ REJIM KUZATILADI, chunki forma XULQI unga bog'liq: maydonlar
    ham, tekshiruvlar ham. `useWatch` birinchi chizishda `undefined`
    qaytaradi (qiymatlar `useEffect` da qo'yiladi) — o'shanda
    `DIRECT_ISAPI` deb qaraladi va bu MAVJUD XULQ: forma ilgari ham
    aynan shu maydonlar bilan ochilardi.
  */
  const connectionType = Form.useWatch('connectionType', form) ?? DEFAULTS.connectionType
  const isGateway = connectionType === 'ISUP_GATEWAY'

  /*
    ⚠️⚠️ TIZIM TAKLIF QILADI, ADMIN XOHLASA O'ZGARTIRADI (egasi qarori,
    «variant C»). Maydonlar tayyor qiymat bilan chiqadi, lekin ular
    TAHRIRLANADI: kamera boshqa tizimda allaqachon ro'yxatdan o'tgan
    bo'lishi va o'sha `EhomeID` ni saqlab qolish kerak bo'lishi mumkin.

    ⚠️ FAQAT YARATISHDA (`device === null`). Tahrirlashda yaratish
    mavjud kalitni jimgina almashtirib yuborardi.

    ⚠️ FAQAT BO'SH maydonga. Operator qiymatni o'zgartirib, keyin
    rejimni u yoqqa-bu yoqqa almashtirsa, terilgan qiymat YO'QOLMASIN.
  */
  useEffect(() => {
    if (!open || device !== null || connectionType !== 'ISUP_GATEWAY') return
    const current = form.getFieldsValue(['isupDeviceId', 'isupKey']) as Partial<FormValues>
    const patch: Partial<FormValues> = {}
    if (!current.isupDeviceId) patch.isupDeviceId = generateIsupDeviceId()
    if (!current.isupKey) patch.isupKey = generateIsupKey()
    if (Object.keys(patch).length > 0) form.setFieldsValue(patch)
  }, [open, device, connectionType, form])

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      device
        ? {
            name: device.name,
            deviceId: device.deviceId,
            connectionType: device.connectionType,
            // ⚠️ `?? ''` / `?? undefined` — gateway qurilmasida bu uchta
            // maydon bo'sh keladi. Ularni `null` bilan to'ldirish antd
            // maydonida "null" so'zi bo'lib ko'rinardi.
            ipAddress: device.ipAddress ?? '',
            port: device.port ?? DEFAULTS.port,
            username: device.username ?? '',
            isupDeviceId: device.isupDeviceId ?? '',
            // ⚠️⚠️ ATAYLAB BO'SH va bu YERDA HECH QACHON YARATILMAYDI.
            // Server kalitni qaytarmaydi (`assertNoSecret`), ya'ni uni
            // oldindan to'ldirish MUMKIN EMAS. Bu yerda yangi qiymat
            // yaratish esa eng zararli xato bo'lardi: operator nomni
            // tuzatib saqlaganda kalit JIMGINA almashib ketardi va
            // kamera qayta ulanmay qolardi — sababi hech qayerda
            // yozilmasdi (parol maydonidagi bilan aynan bir xil qaror).
            isupKey: '',
            // ⚠️ ATAYLAB BO'SH: server parolni qaytarmaydi. Bu yerga
            // biror to'ldiruvchi qiymat ("••••") qo'yish yomonroq bo'lardi
            // — u yuborilib, haqiqiy parolni almashtirib yuborardi.
            password: '',
            timeZone: device.timeZone,
            fdId: device.fdId,
            doorNo: device.doorNo,
            direction: device.direction,
            // ⚠️ OLDINDAN TO'LDIRILADI: usiz forma joylashuvni "bo'sh"
            // deb ochar va saqlash uni TOZALAB yuborardi (`PUT` — to'liq
            // almashtirish).
            siteId: device.siteId,
            webhookAddress: formatWebhookAddress(device.webhookHost, device.webhookPort),
            active: device.active,
          }
        : { ...DEFAULTS, timeZone: defaultTimeZone },
    )
  }, [open, device, form, defaultTimeZone])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      /*
        ⚠️⚠️ SO'ROV TANASI BU YERDA YIG'ILMAYDI — `buildDeviceDraft` /
        `buildDeviceUpdate` da. Sabab `buildDeviceCommon` sarlavhasida:
        `PUT` TO'LIQ ALMASHTIRISH va tushib qolgan maydon jimgina
        TOZALANADI. Komponent ichida yig'ilgan tana chaqirib
        o'lchanmasdi, ya'ni yo'qolgan maydonni faqat jonli o'rnatmada
        sezish mumkin bo'lardi.
      */
      if (device) {
        // ⚠️ `deviceId` YUBORILMAYDI — `DeviceUpdate` da bunday maydon
        // yo'q (belgi o'zgarmaydi, sabab `attendanceApi.ts` da).
        //
        // ⚠️ Javob `{device, result}` (D4) — ikkala yo'l bitta shaklga
        // keltiriladi, aks holda `onSuccess` da tur tekshiruvi kerak
        // bo'lardi va u yangi maydon qo'shilganda jimgina buzilardi.
        const updated = await updateDevice(device.id, buildDeviceUpdate(values, values.active))
        return { device: updated.device, result: updated.result }
      }
      // ⚠️ Yaratishda `active` YUBORILMAYDI: yangi qurilma har doim faol
      // (`device.active DEFAULT TRUE`) va `DeviceDraft` da bunday maydon
      // yo'q. Uni yuborish server jimgina e'tiborsiz qoldiradigan
      // "sozlama" bo'lardi — ya'ni forma yolg'on tanlov ko'rsatardi.
      // ⚠️ Yaratishda holat YO'Q (`null`), qurilma esa bor — shakl bir xil.
      const created = await createDevice(buildDeviceDraft(values, values.deviceId))
      return { device: created, result: null as OperationResult | null }
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all })
      form.resetFields()
      onClose()
      // ⚠️ Saqlangan qurilma YUQORIGA uzatiladi: gateway rejimida sahifa
      // darhol «Ulash ma'lumotlari» oynasini ochadi (props izohi).
      // Natija ham u bilan birga — `PARTIAL` ni ko'rsatish SAHIFANING
      // ishi (props izohi: modal bu paytda allaqachon yopilgan).
      onSaved?.(saved.device, saved.result)
    },
  })

  /*
    ⚠️ «Qayta yaratish» — mavjud qiymatni ALMASHTIRADI, shu sababli u
    ochiq tugma, jimgina ishlaydigan xulq emas. Tahrirlashda ham ochiq:
    kalit sizib ketgan bo'lsa uni almashtirish HAQIQIY ehtiyoj, va u
    o'shanda kameraga qaytadan teriladi.
  */
  /** Gateway rad etgan bo'lsa — tanish sabab, aks holda `null` */
  const gatewayError = save.isError ? gatewayErrorCode(save.error) : null

  const regenerateDeviceId = () => form.setFieldsValue({ isupDeviceId: generateIsupDeviceId() })
  const regenerateKey = () => form.setFieldsValue({ isupKey: generateIsupKey() })

  /** Bekor qilib chiqish — xato xabari ham qolmasin, keyingi qurilma
      oynasida u eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={device ? t('attendance.editTitle') : t('attendance.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {/*
        ⚠️⚠️ GATEWAY RAD ETGANDA SABAB ATAYLAB AYTILADI.

        Gateway `addDevice` da HTTP 200 qaytarib, javob TANASIDA
        `status: "fail"` deyishi mumkin — ya'ni «muvaffaqiyatli» so'rov
        aslida muvaffaqiyatsiz. Sabab bitta texnik so'z bo'lib keladi
        (`deviceExist`) va u operatorga NIMA QILISHNI aytmaydi.

        Shuning uchun tanish sabab uchun ANIQ ko'rsatma qo'shiladi, lekin
        server matni TASHLANMAYDI (`errors.ts` dagi qoida): u sarlavhada
        qoladi, ko'rsatma esa tagida. Sabab tanish bo'lmasa — odatiy yo'l,
        hech qanday xabar yo'qolmaydi.
      */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
          description={
            gatewayError === null ? null : t(`attendance.gatewayErrors.${gatewayError}`)
          }
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={device ? t('attendance.editNote') : t('attendance.createNote')}
        />

        <Form.Item
          name="name"
          label={t('attendance.name')}
          extra={t('attendance.nameHint')}
          rules={[{ required: true, message: t('attendance.validation.nameRequired') }]}
        >
          <Input autoFocus placeholder={t('attendance.namePlaceholder')} />
        </Form.Item>

        {/*
          ⚠️ Belgi (`deviceId`) — QURILMANING SERIYA RAQAMI EMAS, operator
          beradigan barqaror nom. Seriya faqat ulanib bo'lgandan keyin
          ma'lum bo'ladi, yozuv esa undan OLDIN yaratiladi.

          ⚠️ TAHRIRLASHDA MAYDON O'CHIRILGAN (`disabled`), yashirilgan
          EMAS: server uni `DeviceUpdate` da umuman qabul qilmaydi. Butunlay
          olib tashlash "belgi yo'q ekan" degan taassurot berardi, tahrirga
          ochiq qoldirish esa jimgina e'tiborsiz qoldiriladigan maydon
          bo'lardi — operator o'zgartirib, saqlab, hech narsa
          o'zgarmaganini keyin bilardi.
        */}
        <Form.Item
          name="deviceId"
          label={t('attendance.deviceId')}
          extra={
            <ExpandableHint
              text={device ? t('attendance.deviceIdLockedHint') : t('attendance.deviceIdHint')}
              fontSize={14}
            />
          }
          rules={
            device
              ? []
              : [
                  { required: true, message: t('attendance.validation.deviceIdRequired') },
                  {
                    validator: (_, value: string | undefined) =>
                      !value || isValidDeviceCode(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('attendance.validation.deviceIdFormat'))),
                  },
                ]
          }
        >
          {/* ⚠️ C1: bu o'rin ILGARI `"kirish-1"` deb QOTIRILGAN edi —
              inglizcha panelda ham o'zbekcha ko'rinardi. Yordam matni
              (`deviceIdHint`) esa har tilda o'z namunasini beradi
              (`entrance-1` / `vhod-1` / `kirish-1`), ya'ni o'rin bilan
              yordam matni bir-biriga ZID edi. */}
          <Input placeholder={t('attendance.deviceIdPlaceholder')} disabled={device !== null} />
        </Form.Item>

        {/*
          ⚠️⚠️ JOYLASHUV — "BU TERMINAL QAYERDA" DEGAN SAVOLNING YAGONA
          JAVOBI. Manzil (`ipAddress` yoki `isupDeviceId`) unga QANDAY
          borishni aytadi, lekin uni QAYERDAN izlashni aytmaydi —
          `NasFormModal` dagi bilan aynan bir xil farq va aynan shu
          sababdan bu yerda ham bir xil tanlagich (`SiteSelect`)
          ishlatiladi, yangisi yasalmaydi.

          ⚠️ MAYDON ILGARI UMUMAN YO'Q EDI va bu MA'LUMOT YO'QOTARDI:
          `PUT` to'liq almashtirish, ya'ni har saqlash mavjud
          joylashuvni jimgina TOZALAB yuborardi (`buildDeviceCommon`
          sarlavhasi).

          ⚠️ Bo'sh qoldirish MUMKIN va bu "sozlanmagan" degani: ustunda
          `NOT NULL` yo'q, `DeviceDraft.siteId` ixtiyoriy, `SiteRef` esa
          bunday yozuvni ataylab "ko'rsatilmagan" deb chizadi. Tozalash
          `allowClear` bilan — KO'RINADIGAN qaror, tasodif emas.
        */}
        {/* ⚠️ Izoh O'RALADI: uch tildagi eng uzuni 220 belgi, chegara esa
            200 (`tests/tester-bugs.mjs` §5 — brauzerda o'lchangan) */}
        <Form.Item
          name="siteId"
          label={t('attendance.site')}
          extra={<ExpandableHint text={t('attendance.siteHint')} fontSize={14} />}
        >
          <SiteSelect />
        </Form.Item>

        {/*
          ⚠️⚠️ ULANISH TURI — QURILMANING sozlamasi, GLOBAL sozlama EMAS.
          Bitta tashkilotda VPN'li filial ham, VPN'siz filial ham bo'ladi
          va ular bitta ro'yxatda turadi (`DEVICE_CONNECTION_TYPES` izohi).

          ⚠️ Tanlov QOLGAN MAYDONLARNI belgilaydi, shuning uchun u
          ulardan OLDIN turadi: pastdagi maydonlarni to'ldirib bo'lib,
          keyin rejimni almashtirish qilingan ishni bekor qilardi.

          ⚠️ Tahrirlashda ham OCHIQ: filialga VPN tortilishi (yoki
          aksincha, VPN'dan voz kechilishi) haqiqiy hodisa. Uni
          qulflab qo'yish operatorni qurilmani o'chirib, qaytadan
          yaratishga majbur qilardi — va o'shanda butun hodisalar tarixi
          yo'qolardi (`deleteWarn`).
        */}
        <Form.Item
          name="connectionType"
          label={t('attendance.connectionType')}
          extra={<ExpandableHint text={t('attendance.connectionTypeHint')} fontSize={14} />}
          rules={[{ required: true, message: t('attendance.validation.connectionTypeRequired') }]}
        >
          <Select
            options={DEVICE_CONNECTION_TYPES.map((value) => ({
              value,
              label: t(`attendance.connectionTypes.${value}`),
            }))}
          />
        </Form.Item>

        {/*
          ⚠️⚠️ MAYDONLAR REJIMGA QARAB CHIZILADI — va bu ko'zdan
          yashirish emas, CHIZMASLIK. Yashirilgan (`display: none`)
          maydon formada qolardi, tekshiruvi ishlab turardi va qiymati
          so'rovga qo'shilib ketardi; server esa uni
          `device_connection_valid` CHECK cheklovi bilan rad etardi.

          So'rov tanasining kafolati esa bu yerda EMAS —
          `buildConnectionPayload` da: forma do'koni chizilmagan
          maydonning eski qiymatini baribir ushlab qolishi mumkin.
          Ikkalasi ikki xil ish qiladi: bu yerda operator KERAKSIZ
          maydonni ko'rmaydi, u yerda esa server uni OLMAYDI.
        */}
        {isGateway ? (
          <>
            {/*
              ⚠️ BU IKKI QIYMAT KAMERA MENYUSIGA QO'LDA TERILADI. Shuning
              uchun ular chalkashadigan belgisiz yaratiladi (`0/O`, `1/l/I`
              alifbodan chiqarilgan — `SAFE_ALPHABET`) va kalit 32 emas,
              16 belgi (`ISUP_KEY_LENGTH` izohi).

              ⚠️ Maydon TAHRIRLANADI va bu egasining qarori: kamera boshqa
              tizimda ro'yxatdan o'tgan bo'lsa, o'sha `EhomeID` saqlanib
              qolishi kerak.
            */}
            <Form.Item
              name="isupDeviceId"
              label={t('attendance.isupDeviceId')}
              extra={<ExpandableHint text={t('attendance.isupDeviceIdHint')} fontSize={14} />}
              rules={[
                { required: true, message: t('attendance.validation.isupDeviceIdRequired') },
                {
                  validator: (_, value: string | undefined) =>
                    !value || isValidIsupDeviceId(value)
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('attendance.validation.isupDeviceIdFormat'))),
                },
              ]}
            >
              {/* ⚠️ `maxLength` — hujjatdagi chegara (A.25). Uni terish
                  paytida to'xtatish gateway'ning `badParameters` javobidan
                  ancha aniqroq: o'sha javob qaysi maydon ekanini aytmaydi */}
              <Input
                maxLength={ISUP_DEVICE_ID_MAX}
                placeholder="XSIB7K2M9P4Q"
                addonAfter={
                  <Button type="link" size="small" onClick={regenerateDeviceId}>
                    {t('attendance.regenerate')}
                  </Button>
                }
              />
            </Form.Item>

            {/*
              ⚠️ KALIT YULDUZCHA ORTIGA YASHIRILMAYDI — `password`
              maydonidan ATAYLAB farq qiladi. Sabab: uni admin ekrandan
              O'QIB texnikka aytadi. Yashirilgan qiymatni aytib bo'lmaydi,
              ya'ni yashirish bu maydonning butun maqsadini bekor qilardi.

              ⚠️ Shunga qaramay u RO'YXAT javobida kelmaydi va tahrirlashda
              bo'sh chiqadi: bo'sh qoldirilsa kalit O'ZGARMAYDI
              (`buildConnectionPayload`). Ikkalasi zid emas — sir ataylab
              KO'RSATILADI, lekin faqat so'ralganda va audit bilan.
            */}
            <Form.Item
              name="isupKey"
              label={t('attendance.isupKey')}
              extra={
                <ExpandableHint
                  text={device ? t('attendance.isupKeyEditHint') : t('attendance.isupKeyCreateHint')}
                  fontSize={14}
                />
              }
              rules={[
                ...(device
                  ? []
                  : [{ required: true, message: t('attendance.validation.isupKeyRequired') }]),
                {
                  validator: (_, value: string | undefined) =>
                    !value || isValidIsupKey(value)
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('attendance.validation.isupKeyFormat'))),
                },
              ]}
            >
              <Input
                maxLength={ISUP_KEY_MAX}
                autoComplete="off"
                addonAfter={
                  <Button type="link" size="small" onClick={regenerateKey}>
                    {t('attendance.regenerate')}
                  </Button>
                }
              />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              name="ipAddress"
              label={t('attendance.ipAddress')}
              extra={<ExpandableHint text={t('attendance.ipAddressHint')} fontSize={14} />}
              rules={[
                { required: true, message: t('attendance.validation.ipRequired') },
                {
                  validator: (_, value: string | undefined) =>
                    !value || isValidIpv4(value)
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('attendance.validation.ipFormat'))),
                },
              ]}
            >
              <Input placeholder="10.231.169.203" />
            </Form.Item>

            {/* Port ko'pincha 80, lekin ko'prik orqali ulanganda butunlay
                boshqa bo'ladi (o'lchangan holat: `10.20.10.9:18901`) —
                shuning uchun u maydon, qotirilgan qiymat emas */}
            <Form.Item
              name="port"
              label={t('attendance.port')}
              extra={t('attendance.portHint')}
              rules={[{ required: true, message: t('attendance.validation.portRequired') }]}
            >
              <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            </Form.Item>
          </>
        )}

        {/*
          ⚠️⚠️ IKKI MANZIL — VA ULAR BOG'LIQ EMAS. Bu forma 2026-08-11
          da aynan shu sababdan xatoga olib keldi: operator qurilmaning
          O'Z LAN manzilini kiritdi, server esa unga ko'prik orqali
          borardi. Bu «operator adashdi» degani emas — interfeys
          foydalanuvchidan u bilmaydigan narsani so'ragan edi.

              yuqoridagi maydon:  BIZ qurilmaga qanday boramiz
              bu maydon:          QURILMA bizga qanday keladi

          ⚠️ Bo'sh qoldirilsa global sozlama ishlatiladi
          (`xsib.attendance.webhook.callback-host`). Per-qurilma qiymat
          globaldan USTUN turadi: NAT ortidagi qurilma serverni bir
          manzilda, o'sha tarmoqdagi ikkinchisi butunlay boshqasida
          ko'radi.

          ⚠️ Bu maydonni saqlash qurilmaga HECH NARSA yozmaydi — u
          faqat bazaga tushadi. Qurilmaga yozish alohida tugma bilan
          (`attendance.writeWebhook`), sabab `configureWebhook` da.
        */}
        {/*
          ⚠️⚠️ WEBHOOK MANZILI IKKALA REJIMDA HAM KO'RSATILADI —
          2026-08-15 da shu kunning o'zida IKKI MARTA o'zgardi, va
          ikkinchi qaror o'lchovga tayanadi.

          Ertalab bu maydon gateway rejimida YASHIRILDI: o'sha paytda
          `IsupGatewayClient#configureWebhook` "ISUP'da webhook yo'q"
          deb rad etardi, ya'ni maydon jimgina e'tiborsiz qolardi.

          Keyin o'lchandi: manzil QURILMAGA emas, GATEWAY'ning o'ziga
          yozilishi kerak ekan — va u yerda `PUT httpHosts` qabul
          qilinadi (qayta o'qishda tasdiqlandi). Endi kod aynan
          shunday qiladi.

          ⚠️ Nima uchun bu MUHIM: ichki IP o'zgarib turadi. Bu maydon
          bo'lmasa har o'zgarishda serverga kirib sozlama tahrirlash
          kerak bo'lardi. Endi texnik xodim faqat ISUP'ni ulaydi,
          qolgani shu yerdan boshqariladi.

          ⚠️ MA'LUM CHEKLOV — operatorga aytilishi SHART: gateway bu
          manzilni qabul qiladi, lekin sinovda hodisani UZATMADI
          (`IsupGatewayClient#configureWebhook` javadoc'i). ISUP'da
          hodisalar hozircha PULL orqali keladi. Shuning uchun
          gateway rejimida maydon ostida boshqa izoh chiqadi —
          "sozlanadi, lekin push kafolatlanmaydi".
        */}
        <Form.Item
          name="webhookAddress"
          label={t('attendance.webhookAddress')}
          extra={
            <ExpandableHint
              text={
                isGateway
                  ? t('attendance.webhookAddressHintGateway')
                  : t('attendance.webhookAddressHint')
              }
              fontSize={14}
            />
          }
          rules={[
            {
              validator: (_, value: string | undefined) =>
                parseWebhookAddress(value) === null
                  ? Promise.reject(new Error(t('attendance.validation.webhookAddressFormat')))
                  : Promise.resolve(),
            },
          ]}
        >
          <Input placeholder="10.10.10.12:18116" />
        </Form.Item>

        {/*
          ⚠️ HISOB MA'LUMOTLARI HAM FAQAT `DIRECT_ISAPI` DA. Gateway
          rejimida ISAPI hisobi umuman ishlatilmaydi: server qurilmaga
          gateway orqali boradi va u yerda o'zining hisobi bilan
          tanishadi. Bu maydonlarni gateway rejimida ko'rsatish
          operatordan kerak bo'lmagan sirni so'rash bo'lardi — va u
          parolni bekorga tarmoqdan o'tkazardi.
        */}
        {isGateway ? null : (
          <>
            <Form.Item
              name="username"
              label={t('attendance.username')}
              extra={t('attendance.usernameHint')}
              rules={[{ required: true, message: t('attendance.validation.usernameRequired') }]}
            >
              <Input autoComplete="off" placeholder="admin" />
            </Form.Item>

            {/*
              ⚠️ Yordam matni TAHRIRLASHDA boshqacha — bu maydonning butun
              ma'nosi shunda. Yaratishda parol majburiy, tahrirlashda esa
              bo'sh qoldirilsa o'zgarmaydi va buni foydalanuvchi BILISHI kerak
              (komponent sarlavhasidagi izoh).
            */}
            <Form.Item
              name="password"
              label={t('attendance.password')}
              extra={
                <ExpandableHint
                  text={
                    device ? t('attendance.passwordEditHint') : t('attendance.passwordCreateHint')
                  }
                  fontSize={14}
                />
              }
              rules={
                device
                  ? []
                  : [{ required: true, message: t('attendance.validation.passwordRequired') }]
              }
            >
              {/* `visibilityToggle` YO'Q — panel bo'ylab bir xil qoida
                  (`NasFormModal`, `ResyncModal`): parol darajasidagi qiymat
                  ekranda ochilmaydi (yelka orqali o'qish, ekran yozib olish) */}
              <Input.Password visibilityToggle={false} autoComplete="new-password" />
            </Form.Item>
          </>
        )}

        {/*
          ⚠️ QURILMANING zonasi, tenantniki EMAS va serverniki ham emas.
          Qurilma noto'g'ri ofset e'lon qilishi mumkin (2026-08-11 da
          o'lchangan) va bu jimgina noto'g'ri hisobotga olib keladi.
          Ro'yxat YOPIQ: terilishdagi bitta xato butun hisobot kun
          chegarasini siljitardi.
        */}
        <Form.Item
          name="timeZone"
          label={t('attendance.timeZone')}
          extra={<ExpandableHint text={t('attendance.timeZoneHint')} fontSize={14} />}
          rules={[{ required: true, message: t('attendance.validation.timeZoneRequired') }]}
        >
          <Select options={DEVICE_TIME_ZONES.map((zone) => ({ value: zone, label: zone }))} />
        </Form.Item>

        {/* FDID — qurilmadagi yuz kutubxonasi. O'lchangan: `1` → `blackFD`,
            `2` → `infraredFD`. Boshqa modelda boshqacha bo'lishi mumkin,
            shuning uchun sozlanadi

            ⚠️ Izoh O'RALMAYDI: uch tildagi eng uzuni 115 belgi, chegara
            esa 120 (`tests/tester-bugs.mjs` §5 — brauzerda o'lchangan).
            Bu uzunlikda «Batafsil» tugmasi matnning O'ZIDAN ko'proq joy
            egallardi. */}
        <Form.Item
          name="fdId"
          label={t('attendance.fdId')}
          extra={t('attendance.fdIdHint')}
          rules={[{ required: true, message: t('attendance.validation.fdIdRequired') }]}
        >
          <Input placeholder={DEFAULTS.fdId} />
        </Form.Item>

        <Form.Item
          name="doorNo"
          label={t('attendance.doorNo')}
          extra={t('attendance.doorNoHint')}
          rules={[{ required: true, message: t('attendance.validation.doorNoRequired') }]}
        >
          {/* 1..8 — bazadagi `device_door_no_valid` cheklovining aynan o'zi */}
          <InputNumber min={1} max={8} style={{ width: '100%' }} />
        </Form.Item>

        {/*
          ⚠️⚠️ YO'NALISH — QURILMA AYTMAYDIGAN YAGONA NARSA.

          Terminal hodisada "kirdi" yoki "chiqdi" demaydi (o'lchangan
          `minor` kodlari: 75, 76, 38, 21, 22 — birortasida yo'nalish
          yo'q). Ya'ni bu maydon terminal QAYERGA o'rnatilgani haqidagi
          operator bilimi, va usiz hisobot kelish/ketishni skanerlashlar
          TARTIBIDAN taxmin qilishga majbur.

          ⚠️ Standart `BOTH` va u "sozlanmagan" degani EMAS: bitta
          eshikda bitta terminal — odatiy holat, o'shanda yo'nalish
          HAQIQATAN ajratilmaydi. `IN` ni standart qilish har chiqishni
          "ishga kelish" qilib yozardi.

          ⚠️ Ro'yxat YOPIQ va u yaratishda ham, tahrirlashda ham
          chiziladi (`active` dan farqli): yo'nalish terminalning
          o'rnatilgan joyiga bog'liq va u birinchi kundanoq ma'lum.
        */}
        <Form.Item
          name="direction"
          label={t('attendance.direction')}
          extra={<ExpandableHint text={t('attendance.directionHint')} fontSize={14} />}
          rules={[{ required: true, message: t('attendance.validation.directionRequired') }]}
        >
          <Select
            options={DEVICE_DIRECTIONS.map((value) => ({
              value,
              label: t(`attendance.directions.${value}`),
            }))}
          />
        </Form.Item>

        {/*
          ⚠️ "Faol" — o'chirish EMAS. Nofaol qurilma so'ralmaydi (fon
          jarayoni uni chetlab o'tadi), lekin yozuvi va hodisalar tarixi
          joyida qoladi. Ta'mirdagi terminal uchun aynan shu kerak.

          ⚠️ FAQAT TAHRIRLASHDA chiziladi: `DeviceDraft` da bunday maydon
          yo'q va yangi qurilma har doim faol bo'lib yaratiladi. Yaratish
          oynasida ko'rsatish tanlab bo'lmaydigan tanlov bo'lardi.
        */}
        {device === null ? null : (
          <Form.Item
            name="active"
            label={t('attendance.active')}
            valuePropName="checked"
            extra={<ExpandableHint text={t('attendance.activeHint')} fontSize={14} />}
          >
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
